/**
 * stockCatalog.ts — Acervo compartilhado de imagens do Studio
 *
 * Os modelos prontos NÃO gravam o endereço da foto: gravam uma referência
 * `acervo://<chave>`. Quem transforma a referência em endereço é este módulo,
 * usado tanto pelo desenho web quanto pela publicação — assim a mesma foto
 * pode ser trocada no painel WL sem reabrir nenhum modelo.
 *
 * O mapa abaixo é a cópia de segurança embutida (espelho da carga inicial da
 * tabela `studio_stock_assets`): garante que a prévia funcione mesmo antes de
 * o catálogo do banco ser carregado.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, acervo compartilhado — Fase 1)
 */

/** Prefixo usado nas referências gravadas nos blocos. */
export const STOCK_PREFIX = 'acervo://';

/** Monta o endereço de uma foto do Pexels na largura pedida. */
function pexels(id: number, largura: number): string {
  return `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${largura}`;
}

/** Identificadores das fotos da carga inicial (mesma lista da tabela). */
const IDS: Record<string, number> = {
  'capa.cidade-noite': 380769,
  'capa.reuniao': 3184291,
  'capa.equipe': 3184465,
  'capa.escritorio': 3182812,
  'capa.notebook': 3861969,
  'capa.painel': 590041,
  'capa.mesa-trabalho': 265087,
  'capa.atendimento': 1181244,
  'capa.arquitetura': 325185,
  'capa.palestra': 2887661,
  'capa.produto': 1092644,
  'capa.loja': 3184405,
  'capa.celular': 5926382,
  'capa.criativo': 1595385,
  'capa.industria': 4348404,
  'capa.consultoria': 3184418,
  'capa.dados': 3183150,
  'capa.interior': 1103970,
  'capa.time': 3182746,
  'capa.laptop-codigo': 4386370,
  'capa.retrato': 936137,
  'capa.minimal': 3760067,
  'capa.logistica': 7688336,
};

/** Cópia de segurança embutida: chave → endereço da imagem. */
const EMBUTIDO: Record<string, string> = Object.fromEntries(
  Object.entries(IDS).map(([chave, id]) => [chave, pexels(id, 1600)]),
);

/** Catálogo carregado do banco (painel WL manda no conteúdo). */
let catalogo: Record<string, string> = {};

/**
 * Carrega/atualiza o catálogo vindo do banco.
 * Chamado uma única vez por tela; sobrepõe a cópia embutida.
 */
export function setStockCatalog(itens: Record<string, string>): void {
  catalogo = { ...itens };
}

/** Endereço da miniatura de uma chave (usada nas galerias de escolha). */
export function stockThumbUrl(chave: string): string | undefined {
  const id = IDS[chave];
  return id ? pexels(id, 480) : catalogo[chave];
}

/** Lista as chaves conhecidas (banco primeiro, depois a cópia embutida). */
export function listStockKeys(): string[] {
  return Array.from(new Set([...Object.keys(catalogo), ...Object.keys(EMBUTIDO)])).sort();
}

/**
 * Transforma o valor de um campo de imagem no endereço final.
 * Valores que não são referência de acervo passam intactos (endereço digitado,
 * arquivo do próprio cliente, `data:` da importação de HTML).
 */
export function resolveAssetUrl(valor: unknown): string {
  if (typeof valor !== 'string' || !valor) return '';
  if (!valor.startsWith(STOCK_PREFIX)) return valor;

  const chave = valor.slice(STOCK_PREFIX.length);
  return catalogo[chave] ?? EMBUTIDO[chave] ?? '';
}
